import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Shield, CheckCircle2, Clock, MapPin, Tag, Award, ExternalLink, Copy, Share2, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Human-readable event labels
const EVENT_LABEL = {
  'lead.listed': 'Lead listed on marketplace',
  'lead.reserved': 'Buyer reserved lead',
  'lead.purchased': 'Lead purchased',
  'lead.delivered': 'Contact data delivered',
  'lead.accepted': 'Buyer accepted lead',
  'lead.re_verified': 'Lead re-verified',
  'lead.duplicate_flagged': 'Duplicate detection flagged',
  'lead.phone_verified': 'Phone re-verified',
  'lead.email_verified': 'Email re-verified',
};

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function ageLabel(hours) {
  if (hours == null) return '—';
  if (hours < 1) return 'Under 1 hour';
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'}`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'}`;
}

const CheckRow = ({ ok, label }) => (
  <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0" data-testid={`verify-row-${label.toLowerCase().replace(/\s+/g,'-')}`}>
    <span className="text-sm text-gray-700">{label}</span>
    {ok ? (
      <span className="inline-flex items-center gap-1.5 text-[#0044CC] font-medium text-sm">
        <CheckCircle2 className="w-4 h-4" />
        Verified
      </span>
    ) : (
      <span className="inline-flex items-center gap-1.5 text-gray-400 text-sm">
        <XCircle className="w-4 h-4" />
        Not verified
      </span>
    )}
  </div>
);

const Attr = ({ icon: Icon, label, value }) => (
  <div>
    <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-gray-500 mb-1">
      {Icon && <Icon className="w-3.5 h-3.5" />}
      {label}
    </div>
    <div className="text-sm text-gray-900 font-medium">{value ?? '—'}</div>
  </div>
);

export default function PublicPassportPage() {
  const { digitalTitle } = useParams();
  const [passport, setPassport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/public/passport/${digitalTitle}`);
        if (!cancelled) setPassport(res.data);
      } catch (e) {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [digitalTitle]);

  // Inject dynamic <title> + OG meta + JSON-LD once loaded
  useEffect(() => {
    if (!passport) return;
    document.title = passport.meta?.title || `Lead Passport · ${digitalTitle}`;

    const setMeta = (attr, key, value) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', value);
    };
    setMeta('name', 'description', passport.meta?.description || '');
    setMeta('property', 'og:title', passport.meta?.title || '');
    setMeta('property', 'og:description', passport.meta?.description || '');
    setMeta('property', 'og:type', 'article');
    setMeta('property', 'og:url', window.location.href);
    setMeta('name', 'twitter:card', 'summary_large_image');

    // JSON-LD structured data
    let ld = document.getElementById('jsonld-passport');
    if (!ld) {
      ld = document.createElement('script');
      ld.type = 'application/ld+json';
      ld.id = 'jsonld-passport';
      document.head.appendChild(ld);
    }
    ld.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: passport.meta?.title,
      description: passport.meta?.description,
      identifier: digitalTitle,
      category: passport.lead_summary?.category,
      brand: { '@type': 'Organization', name: 'LeadXchange' },
      aggregateRating: passport.quality?.score
        ? { '@type': 'AggregateRating', ratingValue: passport.quality.score, bestRating: 100, ratingCount: 1 }
        : undefined,
    });
    return () => {
      // Restore default title on unmount
      document.title = 'LeadXchange';
    };
  }, [passport, digitalTitle]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-gray-500 font-mono text-sm">Loading Lead Passport…</div>
      </div>
    );
  }
  if (notFound) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6">
        <Shield className="w-14 h-14 text-gray-300 mb-4" />
        <h1 className="font-heading text-2xl text-gray-900 mb-2">Digital Title not found</h1>
        <p className="text-sm text-gray-500 mb-6 max-w-md text-center">
          The Digital Title <code className="font-mono">{digitalTitle}</code> doesn't exist on LeadXchange or has been archived.
        </p>
        <Link to="/" className="text-sm text-[#0044CC] hover:underline">← Back to LeadXchange</Link>
      </div>
    );
  }

  const s = passport.lead_summary || {};
  const v = passport.verification || {};
  const p = passport.provenance || {};
  const q = passport.quality || {};

  const shareUrl = window.location.href;
  const copyLink = () => {
    navigator.clipboard?.writeText(shareUrl);
    toast.success('Link copied');
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="public-passport-page">
      {/* Minimal top bar — anonymous, brand only */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 bg-[#0055FF] rounded flex items-center justify-center">
              <Shield className="w-4 h-4 text-white" />
            </div>
            <span className="font-heading text-lg font-semibold text-gray-900">LeadXchange</span>
          </Link>
          <Link to="/marketplace" className="text-sm text-gray-600 hover:text-[#0044CC]" data-testid="browse-marketplace-link">
            Browse marketplace →
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 lg:px-8 py-10">
        {/* Header */}
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#0055FF]/5 border border-[#0055FF]/20 rounded font-mono text-xs text-[#0044CC] mb-4" data-testid="passport-digital-title">
            <Shield className="w-3.5 h-3.5" />
            Digital Title · {digitalTitle}
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-gray-900">
            Lead Passport
          </h1>
          <p className="text-gray-500 mt-2 max-w-2xl">
            Every lead traded on LeadXchange has a permanent Digital Title and a verifiable
            passport — the equivalent of a Carfax report for lead data. Anonymous by design,
            verified by data, tracked by title.
          </p>
        </div>

        {/* Top grid — summary + quality score */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="md:col-span-2 bg-white border border-gray-200 rounded-lg p-6">
            <div className="text-xs uppercase tracking-[0.2em] text-gray-500 font-mono mb-4">Lead Summary</div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-5">
              <Attr icon={Tag} label="Category" value={s.category} />
              <Attr icon={MapPin} label="Location" value={s.region} />
              <Attr icon={Clock} label="Age" value={ageLabel(s.lead_age_hours)} />
              <Attr icon={Shield} label="Exclusivity" value={s.exclusivity?.replace(/_/g,' ')} />
              <Attr label="Status" value={s.status_label} />
              <Attr label="Intent" value={s.intent_level?.replace(/_/g,' ')} />
              <Attr label="Listed" value={formatDate(s.listed_at)} />
              <Attr label="Sold count" value={s.sold_count ?? 0} />
            </div>
          </div>

          <div className="bg-gradient-to-br from-[#0055FF] to-[#003399] text-white rounded-lg p-6 flex flex-col justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] opacity-80 font-mono">Quality Score</div>
              <div className="font-heading text-6xl font-semibold mt-3" data-testid="passport-quality-score">
                {q.score ?? '—'}<span className="text-3xl opacity-70">/100</span>
              </div>
            </div>
            <div className="mt-4 text-xs opacity-80">
              Deterministic · Auditable · Version {q.version ?? 1}
            </div>
          </div>
        </div>

        {/* Verification checklist */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500 font-mono mb-4">Verification</div>
          <CheckRow ok={v.phone_verified} label="Phone Verified" />
          <CheckRow ok={v.email_verified} label="Email Verified" />
          <CheckRow ok={v.business_verified} label="Seller Business Verified" />
          <CheckRow ok={v.consent_captured} label="Consent Captured" />
          <CheckRow ok={v.duplicate_check === 'passed'} label="Duplicate Check Passed" />
        </div>

        {/* Provenance */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500 font-mono mb-4">Provenance</div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-5">
            <Attr label="Source" value={p.source_label} />
            <Attr label="Campaign type" value={p.campaign_type ?? '—'} />
            <Attr label="Source evidence recorded" value={p.has_source_evidence ? 'Yes' : 'No'} />
            <Attr label="Seller alias" value={passport.seller_alias} />
          </div>
        </div>

        {/* History */}
        {passport.public_history?.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6" data-testid="passport-history">
            <div className="text-xs uppercase tracking-[0.2em] text-gray-500 font-mono mb-4">Verification History</div>
            <div className="space-y-3">
              {passport.public_history.map((h, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#0055FF] mt-2 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="text-sm text-gray-900">{EVENT_LABEL[h.event] || h.event}</div>
                    <div className="text-xs text-gray-500 font-mono mt-0.5">{formatDate(h.at)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Trust footer */}
        <div className="bg-gray-900 text-white rounded-lg p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <Award className="w-8 h-8 text-[#D4AF37] flex-shrink-0" />
            <div>
              <div className="font-heading text-lg font-semibold">Anonymous by design. Verified by data. Tracked by title.</div>
              <div className="text-sm opacity-75 mt-1">
                LeadXchange assigns a permanent Digital Title to every lead. No PII on public views. Contact data only unlocks after successful platform-settled purchase.
              </div>
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={copyLink}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-700 rounded hover:bg-white/5 transition-colors"
              data-testid="copy-link-btn"
            >
              <Copy className="w-3.5 h-3.5" />
              Copy link
            </button>
            <Link
              to="/marketplace"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-[#0055FF] hover:bg-[#0044CC] rounded transition-colors"
              data-testid="footer-marketplace-link"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              View on marketplace
            </Link>
          </div>
        </div>

        <div className="mt-6 text-center text-xs text-gray-400 font-mono">
          LX-2026 · Public Passport · No PII exposed on this page
        </div>
      </div>
    </div>
  );
}
